import { sortBy } from 'lodash';

import {
  ExecutionStatusEnum,
  getFragmentData,
  ServiceUsagesSourceEnum,
} from '@e-socna/data-layer';
import type { ResultOf } from '@graphql-typed-document-node/core';
import { ensureDefined, isDefined } from '@e-socna/utils';
import type { GroupingModeEnum, Id, TotalModeEnum } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type {
  ResolverHookResult,
  ResolverParameters,
} from '../../types/resolvers.type';

import { resolveServiceUsagesSource } from '../utils/resolve-service-usages-source.util';
import { getSubMissionTitles } from '../utils/mission-service-usages-groups.util';
import { hasSubMissions } from '../../../../app/utils/mission-kind-profile.util';

import { DocumentEditorResolvers_QuickAccess_ServicesInfo_Fragment } from '../fragments/quick-access.fragment';

import { useMissionQuickAccess } from './useMissionQuickAccess.hook';

interface Amount {
  value: number;
  currencyId: Id;
}

interface Quantity {
  value: number;
  unitId: Id;
}

export interface ServiceUsageSettings {
  showAmountsInQuotation: boolean;
  showCostsInQuotation: boolean;
  showMaterialsInQuotation: boolean;
  showQuantitiesInQuotation: boolean;
}

interface ServiceValue {
  priceExcludingVat: Amount;
  discountPercentage?: number | null;
  discountAmount?: Amount | null;
  vatId: Id;
}

export interface MaterialUsageInfo {
  readonly _id: Id;
  readonly material: Readonly<{
    code: string;
    title?: string | null;
    label: string;
    content?: string | null;
  }>;
  readonly quantity: Readonly<Quantity>;
}

export interface CostMarginInfo {
  readonly label: string;
  readonly variableValue?: number | null;
  readonly fixedValue?: Readonly<Amount> | null;
}

export interface CostUsageInfo {
  readonly _id: Id;
  readonly cost: Readonly<{
    code: string;
    title?: string | null;
    label: string;
    content?: string | null;
    costMargin: CostMarginInfo;
    amount?: Readonly<Amount> | null;
    value?: number | null;
  }>;
  readonly quantity: Readonly<Quantity>;
}

export interface ServiceUsage {
  readonly _id: Id;
  readonly service: Readonly<{
    code: string;
    title?: string | null;
    content?: string | null;
    // Null when the user may not read the amounts.
    value: Readonly<ServiceValue> | null;
    costUsages: readonly CostUsageInfo[];
    materialUsages: readonly MaterialUsageInfo[];
  }>;
  readonly quantity: Readonly<Quantity>;
  readonly settings: Readonly<ServiceUsageSettings>;
}

export interface ServiceUsagesGroup {
  readonly _id: Id;
  // The sub-mission a group of a collaboration comes from (null for its own
  // groups and for the groups of any other mission): one section each.
  readonly sectionTitle: string | null;
  readonly label: string | null;
  readonly groupingMode: GroupingModeEnum;
  readonly isOptional: boolean;
  readonly totalMode: TotalModeEnum;
  readonly effectiveTotal: Readonly<{ value: number; currencyId: Id }> | null;
  readonly totals: GroupTotals | null;
  readonly serviceUsages: readonly ServiceUsage[];
}

type GroupEffectiveTotal = Readonly<{ value: number; currencyId: Id }>;

export type GroupTotals = Readonly<{
  undiscountedAmountExcludingVat: GroupEffectiveTotal;
  discountAsAmount: GroupEffectiveTotal;
  discountAsPercentage: number;
  discountedAmountExcludingVat: GroupEffectiveTotal;
  discountedAmountIncludingVat: GroupEffectiveTotal;
  vatAsAmount: GroupEffectiveTotal;
  vatAsPercentage: number;
}>;

interface GroupTotalCalculation {
  readonly groupId: Id;
  // Null when the user may not read the amounts.
  readonly totalCalculation: Readonly<{
    result: GroupTotals | null;
  }> | null;
}

const buildTotalsByGroupId = (
  groupTotalCalculations: readonly GroupTotalCalculation[],
): ReadonlyMap<Id, GroupTotals> =>
  new Map(
    groupTotalCalculations
      .map(({ groupId, totalCalculation }): [Id, GroupTotals] | null =>
        isDefined(totalCalculation?.result)
          ? [groupId, totalCalculation.result]
          : null,
      )
      .filter(isDefined),
  );

const buildEffectiveTotalByGroupId = (
  groupTotalCalculations: readonly GroupTotalCalculation[],
): ReadonlyMap<Id, GroupEffectiveTotal> => {
  const entries = groupTotalCalculations
    .map(({ groupId, totalCalculation }): [Id, GroupEffectiveTotal] | null =>
      isDefined(totalCalculation?.result)
        ? [groupId, totalCalculation.result.discountedAmountExcludingVat]
        : null,
    )
    .filter(isDefined);

  return new Map(entries);
};

interface Result {
  serviceUsagesGroups: readonly ServiceUsagesGroup[];
  source: ServiceUsagesSourceEnum;
  totalCalculation: Readonly<{
    details: readonly Readonly<{
      fr: string;
      en: string;
    }>[];
    result?: Readonly<{
      discountAsAmount: Amount;
      discountAsPercentage: number;
      discountedAmountExcludingVat: Amount;
      discountedAmountIncludingVat: Amount;
      undiscountedAmountExcludingVat: Amount;
      vatAsAmount: Amount;
      vatAsPercentage: number;
    }> | null;
    status: ExecutionStatusEnum;
  }>;
}

type TotalCalculationResult = Result['totalCalculation'];

const CONFIDENTIAL_TOTAL_CALCULATION: TotalCalculationResult = {
  details: [{ fr: 'Montants confidentiels', en: 'Confidential amounts' }],
  result: null,
  status: ExecutionStatusEnum.Ko,
};

type LiveServicesInfo = ResultOf<
  typeof DocumentEditorResolvers_QuickAccess_ServicesInfo_Fragment
>;

export const useFullyConsolidatedServiceUsages = (
  parameters: ResolverParameters,
): ResolverHookResult<Result> => {
  const { quotationWorkflowsIdMap } = useReferencesContext();

  const quickAccess = useMissionQuickAccess(parameters);

  if (!quickAccess.isSuccess) {
    return {
      ...quickAccess,
      data: undefined,
    };
  }

  const { data } = ensureDefined({
    data: quickAccess.data,
  });

  const { servicesInfo, quotationInfo } = data;

  const mapLiveGroups = (
    liveServicesInfo: LiveServicesInfo,
    sectionTitle: string | null,
  ): readonly ServiceUsagesGroup[] => {
    const effectiveTotalByGroupId = buildEffectiveTotalByGroupId(
      liveServicesInfo.serviceUsagesGroupTotalCalculations,
    );
    const totalsByGroupId = buildTotalsByGroupId(
      liveServicesInfo.serviceUsagesGroupTotalCalculations,
    );

    return sortBy(
      liveServicesInfo.consolidation.serviceUsagesGroups,
      'index',
    ).map((group) => ({
      _id: group._id,
      sectionTitle,
      label: group.label,
      groupingMode: group.groupingMode,
      isOptional: group.isOptional,
      totalMode: group.totalMode,
      effectiveTotal: effectiveTotalByGroupId.get(group._id) ?? null,
      totals: totalsByGroupId.get(group._id) ?? null,
      serviceUsages: group.serviceUsages.map((usage) => {
        const { quantity, settings, service } = usage.compiled.consolidation;

        return {
          _id: usage._id,
          service: {
            code: service.code,
            title: service.title,
            content: service.content,
            value: service.value,
            costUsages: service.costUsages,
            materialUsages: service.materialUsages,
          },
          quantity,
          settings,
        };
      }),
    }));
  };

  const subMissionTitles = getSubMissionTitles(data);

  const returnFromServicesInfo = (): Result => ({
    source: ServiceUsagesSourceEnum.ServicesInfo,
    // A collaboration prices the groups of its sub-missions, then its own.
    serviceUsagesGroups: [
      ...data.subMissions.flatMap((subMission) =>
        mapLiveGroups(
          getFragmentData(
            DocumentEditorResolvers_QuickAccess_ServicesInfo_Fragment,
            subMission.servicesInfo,
          ),
          subMissionTitles.get(subMission._id) ?? null,
        ),
      ),
      ...mapLiveGroups(servicesInfo, null),
    ],
    totalCalculation:
      servicesInfo.totalCalculation ?? CONFIDENTIAL_TOTAL_CALCULATION,
  });

  const returnFromQuotationInfo = (): Result => {
    const { definedQuotationInfo } = ensureDefined({
      definedQuotationInfo: quotationInfo,
    });

    const { snapshot } = ensureDefined({
      snapshot: definedQuotationInfo.snapshot,
    });

    const effectiveTotalByGroupId = buildEffectiveTotalByGroupId(
      snapshot.serviceUsagesGroupTotalCalculations,
    );
    const totalsByGroupId = buildTotalsByGroupId(
      snapshot.serviceUsagesGroupTotalCalculations,
    );

    return {
      source: ServiceUsagesSourceEnum.QuotationInfo,
      // The snapshot of a collaboration is already ordered by section, and the
      // groups of its sub-missions share their indexes.
      serviceUsagesGroups: (hasSubMissions(data.kind)
        ? snapshot.serviceUsagesGroups
        : sortBy(snapshot.serviceUsagesGroups, 'index')
      ).map((group) => ({
        _id: group._id,
        sectionTitle: group.subMissionId
          ? (subMissionTitles.get(group.subMissionId) ?? null)
          : null,
        label: group.label,
        groupingMode: group.groupingMode,
        isOptional: group.isOptional,
        totalMode: group.totalMode,
        effectiveTotal: effectiveTotalByGroupId.get(group._id) ?? null,
        totals: totalsByGroupId.get(group._id) ?? null,
        serviceUsages: group.serviceUsages.map((usage) => {
          const { quantity, settings, service } = usage.consolidation;

          return {
            _id: usage._id,
            service: {
              code: service.code,
              title: service.title,
              content: service.content,
              value: service.value,
              costUsages: service.costUsages,
              materialUsages: service.materialUsages,
            },
            quantity,
            settings,
          };
        }),
      })),
      totalCalculation: snapshot.totalCalculation
        ? {
            details: [
              {
                fr: 'Données issues du devis',
                en: 'Data from quotation',
              },
            ],
            result: snapshot.totalCalculation.result,
            status: ExecutionStatusEnum.Ok,
          }
        : CONFIDENTIAL_TOTAL_CALCULATION,
    };
  };

  const { source, serviceUsagesGroups, totalCalculation } =
    resolveServiceUsagesSource({
      quotationInfo,
      quotationWorkflowsIdMap,
    }) === ServiceUsagesSourceEnum.ServicesInfo
      ? returnFromServicesInfo()
      : returnFromQuotationInfo();

  return {
    data: {
      serviceUsagesGroups,
      source,
      totalCalculation,
    },
    error: null,
    isSuccess: true,
    isLoading: false,
  };
};
